<template>
    <div
        :id="id"
        class="lni-c-janitorial-workload-calculator"
    >
        <lni-fixed-action-button
            :id="`${id}_back-to-top`"
            :label="content?.backToTop"
        />
        <div
            v-show="isLoading"
            class="lni-u-text--center"
        >
            <lni-spinner
                :id="`${id}_page-spinner`"
            />
        </div>
        <div class="lni-u-full-width lni-u-flex lni-u-justify-end">
            <button
                class="lni-u-link-style lni-u-heading--5"
                @click="toggleAccordions(allAccordionsCollapsed)"
            >
                <template v-if="allAccordionsCollapsed">
                    <span
                        class="lnicon--caret--down"
                        aria-hidden="true"
                    />
                    {{ content?.expandLabel }}
                </template>
                <template v-else>
                    <span
                        class="lnicon--caret--right"
                        aria-hidden="true"
                    />
                    {{ content?.collapseLabel }}
                </template>
            </button>
        </div>
        <div class="lni-u-pb2 lni-u-bb lni-u-b--subtle">
            <lni-disclosure
                :id="`${id}_job-plan_accordion`"
                class="lni-u-mt2 --accordion"
                :isOpen="true"
                @toggle="checkAllAccordionsCollapsed"
            >
                <template slot="indicator">
                    <span
                        aria-hidden="true"
                        class="lnicon--caret--right lni-u-type--lg lni-u-ma0 lni-u-text--center"
                    />
                </template>
                <span
                    slot="toggle"
                    class="lni-u-heading--2 lni-u-pl1"
                >
                    {{ content?.jobPlanHeading }}
                </span>
                <template slot="content">
                    <div class="--row lni-u-gap2 lni-u-mt2">
                        <div class="lni-c-box--content --card --col__1of3">
                            <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                                {{ content?.jobPlanNameLabel }}
                            </p>
                            <span class="--label">
                                {{ name }}
                            </span>
                        </div>
                        <div class="lni-c-box--content --card --col__1of3">
                            <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                                {{ content?.totalTasks }}
                            </p>
                            <span class="--label --number">
                                {{ jobPlan?.length }}
                            </span>
                        </div>
                        <div class="lni-c-box--content --card --col__1of3">
                            <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                                {{ content?.totalHours }}
                            </p>
                            <span class="--label --number">
                                {{ hoursPlannedTotal }}
                            </span>
                        </div>
                    </div>
                    <lni-janitorial-job-plan-table
                        :id="`${id}_job-plan-table`"
                        :items="jobPlan"
                        :editable="false"
                    />
                    <div class="lni-u-mt2 lni-u-mb3">
                        <a
                            href="#"
                            @click="goTo('/job-plan')"
                        >
                            <span
                                class="lnicon--new-message lni-u-pr1"
                                aria-hidden="true"
                            />
                            {{ content?.editText }} <span class="lni-u-heading--5 lni-u-text--all-caps">{{ name }}</span>
                        </a>
                    </div>
                </template>
            </lni-disclosure>
            <lni-disclosure
                v-if="isManagementReport || isErgonomicEvaluation"
                :id="`${id}_complaints_accordion`"
                class="--accordion"
                @toggle="checkAllAccordionsCollapsed"
            >
                <template slot="indicator">
                    <span
                        aria-hidden="true"
                        class="lnicon--caret--right lni-u-type--lg lni-u-ma0 lni-u-text--center"
                    />
                </template>
                <span
                    slot="toggle"
                    class="lni-u-heading--2 lni-u-pl1"
                >
                    {{ content?.complaintsHeading }}
                </span>
                <template slot="content">
                    <div class="lni-u-reading-width--max lni-u-mt2">
                        <lni-checkbox-list
                            :id="`${id}_complaints`"
                            :labelText="content?.complaintsLabel"
                            :options="content?.complaintOptions"
                            @change="updateComplaints"
                        />
                        <lni-checkbox-list
                            :id="`${id}_regions`"
                            class="lni-u-mv3"
                            :labelText="content?.regionsLabel"
                            :options="content?.regionOptions"
                            @change="updateComplaints"
                        />
                    </div>
                </template>
            </lni-disclosure>
            <lni-disclosure
                v-if="isErgonomicEvaluation"
                :id="`${id}_discomfort-survey_accordion`"
                class="--accordion"
                @toggle="checkAllAccordionsCollapsed"
            >
                <template slot="indicator">
                    <span
                        aria-hidden="true"
                        class="lnicon--caret--right lni-u-type--lg lni-u-ma0 lni-u-text--center"
                    />
                </template>
                <span
                    slot="toggle"
                    class="lni-u-heading--2 lni-u-pl1"
                >
                    {{ content?.discomfortSurveyHeading }}
                </span>
                <div
                    slot="content"
                    class="lni-u-mt2 lni-u-flex lni-u-flex-wrap"
                >
                    <div class="lni-u-reading-width--max lni-u-mb3">
                        {{ content?.discomfortSurveyIntro }}
                    </div>
                    <lni-janitorial-shift-discomfort
                        :id="`${id}_discomfort-survey_start`"
                        classString="lni-u-mr4 lni-u-mb3"
                        :heading="content?.discomfortSurveyStart"
                        @change="updateShiftStartDiscomfort"
                    />
                    <lni-janitorial-shift-discomfort
                        :id="`${id}_discomfort-survey_end`"
                        classString="lni-u-mb3"
                        :heading="content?.discomfortSurveyEnd"
                        @change="updateShiftEndDiscomfort"
                    />
                </div>
            </lni-disclosure>
            <lni-janitorial-work-pace
                :id="`${id}_work-pace`"
                :jobPlan="jobPlan"
                :paceComplaint="complaints.includes('pace')"
                @toggle="checkAllAccordionsCollapsed"
            />
            <lni-janitorial-overall-workload
                :id="`${id}_overall-workload`"
                :jobPlan="jobPlan"
                :lowerExtremityDiscomfortStart="shiftStartDiscomfort?.lowerExtremity"
                :lowerExtremityDiscomfortEnd="shiftEndDiscomfort?.lowerExtremity"
                :overallDiscomfortStart="shiftStartDiscomfort?.overall"
                :overallDiscomfortEnd="shiftEndDiscomfort?.overall"
                :walkingComplaint="complaints.includes('walking')"
                :overallComplaint="complaints.includes('overall')"
                @toggle="checkAllAccordionsCollapsed"
            />
            <lni-disclosure
                :id="`${id}_hand-wrist-risk_accordion`"
                class="--accordion"
                @toggle="checkAllAccordionsCollapsed"
            >
                <template slot="indicator">
                    <span
                        aria-hidden="true"
                        class="lnicon--caret--right lni-u-type--lg lni-u-ma0 lni-u-text--center"
                    />
                </template>
                <span
                    slot="toggle"
                    class="lni-u-heading--2 lni-u-pl1"
                >
                    {{ content?.handWristHeading }}
                </span>
                <template slot="content">
                    <lni-janitorial-risk-hand-wrist
                        :id="`${id}_hand-wrist-risk`"
                        classString="lni-u-mt2 lni-u-mb3"
                        :complaint="complaints.includes('hand/wrist')"
                        :jobPlan="jobPlan"
                        :shiftStartDiscomfort="shiftStartDiscomfort?.handWrist"
                        :shiftEndDiscomfort="shiftEndDiscomfort?.handWrist"
                        @toggle="checkAllAccordionsCollapsed"
                    />
                </template>
            </lni-disclosure>
            <lni-janitorial-risk-region
                :id="`${id}_shoulder-risk`"
                :complaint="complaints.includes('shoulder')"
                :jobPlan="jobPlan"
                :name="content?.shoulderLabel"
                :region="regions.Shoulder"
                :shiftStartDiscomfort="shiftStartDiscomfort?.shoulder"
                :shiftEndDiscomfort="shiftEndDiscomfort?.shoulder"
                @toggle="checkAllAccordionsCollapsed"
            />
            <lni-janitorial-risk-region
                :id="`${id}_back-risk`"
                :complaint="complaints.includes('lower back')"
                :jobPlan="jobPlan"
                :name="content?.backLabel"
                :region="regions.Back"
                :shiftStartDiscomfort="shiftStartDiscomfort?.lowerBack"
                :shiftEndDiscomfort="shiftEndDiscomfort?.lowerBack"
            />
        </div>
        <div class="lni-u-mt3">
            <button
                class="lni-c-button lni-u-text--all-caps lni-u-mr1"
                @click="print"
            >
                <span
                    class="lnicon--print"
                    aria-hidden="true"
                />
                {{ `${content?.printText} ${ isJobPlanReport || isManagementReport ? ` ${content.reportText}` : ` ${content?.evaluationText}` }` }}
            </button>
            <button
                class="lni-c-button --alternate lni-u-text--all-caps"
                @click="$router.push('/')"
            >
                <span
                    class="lnicon--home"
                    aria-hidden="true"
                />
                {{ content?.homeLabel }}
            </button>
        </div>
    </div>
</template>

<script>
import {
    mapGetters,
} from 'vuex';

import Regions from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/enums/regions';

import {
    hoursPlannedTotal,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/calculations';

import {
    timeByHours,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/helpers';

import LniJanitorialOverallWorkload from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/lni-janitorial-calculations/components/lni-janitorial-overall-workload.component.vue';
import LniJanitorialRiskHandWrist from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/lni-janitorial-calculations/components/lni-janitorial-risk-hand-wrist.component.vue';
import LniJanitorialRiskRegion from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/lni-janitorial-calculations/components/lni-janitorial-risk-region.component.vue';
import LniJanitorialWorkPace from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/lni-janitorial-calculations/components/lni-janitorial-work-pace.component.vue';

export default {
    name: 'LniJanitorialCalculations',
    components: {
        LniJanitorialOverallWorkload,
        LniJanitorialRiskHandWrist,
        LniJanitorialRiskRegion,
        LniJanitorialWorkPace,
    },
    data() {
        return {
            accordionIds: [],
            allAccordionsCollapsed: true,
            complaints: [],
            shiftEndDiscomfort: -1,
            shiftStartDiscomfort: -1,
        };
    },
    computed: {
        hoursPlannedTotal() {
            return timeByHours(hoursPlannedTotal(this.jobPlan), 0);
        },
        regions() {
            return Regions;
        },
        ...mapGetters({
            content: 'appState/content',
            isErgonomicEvaluation: 'appState/isErgonomicEvaluation',
            isJobPlanReport: 'appState/isJobPlanReport',
            isLoading: 'appState/isLoading',
            isManagementReport: 'appState/isManagementReport',
            jobPlan: 'appState/jobPlan',
            name: 'appState/name',
        }),
    },
    mounted() {
        this.$store.commit('appState/SET_VALUE', {
            key: 'isLoading',
            value: false,
        }, {
            root: true,
        });

        this.$nextTick(() => {
            let ids = [
                `${this.id}_job-plan_accordion`,
                `${this.id}_work-pace_accordion`,
                `${this.id}_overall-workload_accordion`,
                `${this.id}_hand-wrist-risk_accordion`,
                `${this.id}_shoulder-risk_accordion`,
                `${this.id}_back-risk_accordion`,
            ];
            if (this.isManagementReport) {
                ids = [
                    ...ids,
                    `${this.id}_complaints_accordion`,
                ];
            } else if (this.isErgonomicEvaluation) {
                ids = [
                    ...ids,
                    `${this.id}_complaints_accordion`,
                    `${this.id}_discomfort-survey_accordion`,
                ];
            }
            this.accordionIds = ids;
        });
    },
    methods: {
        checkAllAccordionsCollapsed() {
            let result = true;
            this.accordionIds.forEach(id => {
                result = result && !this.$store.state[id].isOpen;
            });

            this.allAccordionsCollapsed = result;
        },
        goTo(path) {
            this.$store.commit('appState/SET_VALUE', {
                key: 'isLoading',
                value: true,
            }, {
                root: true,
            });

            setTimeout(() => {
                this.$router.push(path);
            });
        },
        print() {
            window.print();
        },
        toggleAccordions(value) {
            this.accordionIds.forEach(id => {
                this.$store.commit('setAttribute', {
                    id,
                    attribute: 'isOpen',
                    value,
                }, {
                    root: true,
                });
            });

            this.checkAllAccordionsCollapsed();
        },
        updateComplaints() {
            const complaints = this.$store.state[`${this.id}_complaints`].value;
            const regions = this.$store.state[`${this.id}_regions`].value;
            this.complaints = [
                this.complaints,
                ...complaints ? complaints : [],
                ...regions ? regions : [],
            ];
        },
        updateShiftEndDiscomfort(data) {
            this.shiftEndDiscomfort = data;
        },
        updateShiftStartDiscomfort(data) {
            this.shiftStartDiscomfort = data;
        },
    },
};
</script>